import type { EmployeeStatus } from "@/lib/api";
import type { TransitionAction } from "@/lib/staff-api";
import { EMPLOYEE_STATUSES } from "@/lib/roster";

/** A one-click act on a dashboard row: the button names what Mark did in the
 *  real world, and `lands` is the stage the employee moves to. */
export interface RowAction {
  action: Extract<TransitionAction, "advance" | "entered" | "badge_made" | "delivered">;
  label: string;
  lands: EmployeeStatus;
}

/** The one primary action per stage (spec transition table). Verification and
 *  a fingerprint result need a date, so they have forms, not entries here. */
export const ROW_ACTIONS: Partial<Record<EmployeeStatus, RowAction>> = {
  paid: { action: "advance", label: "Sent to BIM", lands: "directed_to_bim" },
  directed_to_bim: { action: "advance", label: "Fingerprint appointment scheduled", lands: "fingerprint_appointment" },
  background_cleared: { action: "entered", label: "Mark entered in Virtus + Clearinghouse", lands: "entered_in_systems" },
  entered_in_systems: { action: "badge_made", label: "Badge made", lands: "badge_issued" },
  badge_issued: { action: "delivered", label: "Delivered", lands: "badge_delivered" },
};

/** Where the row's primary action lands, for the stepper; null where staff
 *  have no action. */
export function landingStatus(status: EmployeeStatus): EmployeeStatus | null {
  if (status === "attested_pending_verification" || status === "fingerprint_appointment") {
    return "background_cleared";
  }
  return ROW_ACTIONS[status]?.lands ?? null;
}

// The server's set_stage rules (staff-employees.ts planSetStage), mirrored so
// the dialog never offers a move the server refuses.
const CLEARED_OR_LATER: readonly EmployeeStatus[] = [
  "background_cleared",
  "entered_in_systems",
  "badge_issued",
  "badge_delivered",
];
const BADGE_MADE_OR_LATER: readonly EmployeeStatus[] = ["badge_issued", "badge_delivered"];
/** Attested employees whose full background check is not paid for. */
const ATTESTED_UNPAID: readonly EmployeeStatus[] = ["attested_pending_verification", "attestation_not_found"];
/** Reachable from ATTESTED_UNPAID, or with a completion unpaid, only through the
 *  vendor's completion payment. */
const BCB_PRE_CLEARANCE: readonly EmployeeStatus[] = ["paid", "directed_to_bim", "fingerprint_appointment"];

/** What setStageTargets reads off the employee. */
export interface StageSubject {
  track?: "bcb" | "attested";
  completionPriceOwed?: number;
}

/** Attested with the completion unpaid: the completion clears completionPriceOwed. */
function completionUnpaid(employee: StageSubject): boolean {
  return employee.track === "attested" && typeof employee.completionPriceOwed === "number";
}

/**
 * The stages "Set a different stage" offers from `from`, in pipeline order.
 * Never attestation_not_found ("not found" sets it, with its email); back to
 * attested_pending_verification only while an attested employee's completion is
 * unpaid (the undo of a mistaken "Found" or "Not found"); never the paid track before clearance for
 * an attested employee awaiting verification or one whose completion is unpaid.
 */
export function setStageTargets(from: EmployeeStatus, employee: StageSubject): EmployeeStatus[] {
  const paidTrackClosed = ATTESTED_UNPAID.includes(from) || completionUnpaid(employee);
  return EMPLOYEE_STATUSES.filter(
    (to) =>
      to !== from &&
      to !== "attestation_not_found" &&
      !(to === "attested_pending_verification" && !completionUnpaid(employee)) &&
      !(paidTrackClosed && BCB_PRE_CLEARANCE.includes(to)),
  );
}

export function stageNeedsClearanceDate(to: EmployeeStatus, current: { clearanceDate?: string }): boolean {
  return CLEARED_OR_LATER.includes(to) && !current.clearanceDate;
}

export function stageNeedsPhoto(to: EmployeeStatus, current: { photoOnFile?: boolean }): boolean {
  return BADGE_MADE_OR_LATER.includes(to) && current.photoOnFile !== true;
}

/** A move back before background_cleared removes the recorded clearance date
 *  on the server (the correction keeps a record of it). */
export function stageClearsClearance(to: EmployeeStatus, current: { clearanceDate?: string }): boolean {
  return !CLEARED_OR_LATER.includes(to) && Boolean(current.clearanceDate);
}

/** Today's local calendar date (YYYY-MM-DD): the latest clearance date the date
 *  inputs accept. The server refuses one after today (UTC) or before 2000. */
export function latestClearanceDate(now: Date = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}
